import type { ModuleName } from '@contexts/modules-provider';

import { useCobaltLayoutContext } from '@cae/cobalt-react/components/layout';
import { trackHeapEvent } from '@configs/heap';
import { useModules } from '@contexts/modules-provider';
import { MODULE_PATHS } from '@routes/routes.constants';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ModuleSwitcherCollapsed } from './components/module-switcher-collapsed/ModuleSwitcherCollapsed';
import { ModuleSwitcherExpanded } from './components/module-switcher-expanded/ModuleSwitcherExpanded';

const ModuleSwitcher = React.memo((): React.ReactElement | null => {
  const { modules, selectedModule, hasMultipleModules, selectModule } =
    useModules();
  const { isSideNavigationOpen } = useCobaltLayoutContext();
  const navigate = useNavigate();
  const location = useLocation();

  const handleModuleSwitcherToggle = React.useCallback((): void => {
    if (!location.pathname.startsWith(`${MODULE_PATHS.MANAGE}/`)) {
      return;
    }

    trackHeapEvent('Manage - InstrNavBar - Click - SelectionArrow', {
      elementId: 'navPlatformSelect',
      source: 'host',
      appArea: 'manage',
      route: location.pathname,
    });
  }, [location.pathname]);

  const handleModuleSelect = React.useCallback(
    async (moduleName: ModuleName): Promise<void> => {
      await selectModule(moduleName);
      navigate('/app-initializer', { replace: true });
    },
    [selectModule, navigate]
  );

  if (!selectedModule) return null;

  const props = {
    selectedModule,
    modules,
    hasMultipleModules,
    onModuleSelect: handleModuleSelect,
    onModuleSwitcherToggle: handleModuleSwitcherToggle,
  };

  return isSideNavigationOpen ? (
    <ModuleSwitcherExpanded {...props} />
  ) : (
    <ModuleSwitcherCollapsed {...props} />
  );
});

ModuleSwitcher.displayName = 'ModuleSwitcher';

export { ModuleSwitcher };
